/**
 * Live updates.
 *
 * One connection for the whole app, opened once a session exists and shared by
 * every screen. The server sends the name of what changed and nothing else, so
 * a screen answers by reloading through the ordinary API — which means what
 * arrives has passed the same permission checks as everything else, and a
 * screen that was already correct does no work it did not need to.
 *
 * The browser reconnects on its own when the connection drops, so nothing here
 * has to think about retries; a tab that was asleep reloads on waking.
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

export type Topic = 'schedule' | 'notifications' | 'messages' | 'records';

type Handler = () => void;

interface LiveApi {
  subscribe: (topic: Topic, handler: Handler) => () => void;
}

const LiveContext = createContext<LiveApi | null>(null);

export function LiveProvider({ enabled, children }: { enabled: boolean; children: ReactNode }): JSX.Element {
  const handlers = useRef(new Map<Topic, Set<Handler>>());

  useEffect(() => {
    if (!enabled) return;
    const source = new EventSource('/api/events', { withCredentials: true });

    const fire = (topic: Topic) => () => {
      for (const handler of handlers.current.get(topic) ?? []) handler();
    };
    const onSchedule = fire('schedule');
    const onNotifications = fire('notifications');
    const onMessages = fire('messages');
    const onRecords = fire('records');
    source.addEventListener('schedule', onSchedule);
    source.addEventListener('notifications', onNotifications);
    source.addEventListener('messages', onMessages);
    source.addEventListener('records', onRecords);

    // A tab that has been in the background may have missed a reconnection
    // window entirely. Coming back to it, assume everything is stale.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        onSchedule(); onNotifications(); onMessages(); onRecords();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      source.close();
    };
  }, [enabled]);

  const api: LiveApi = {
    subscribe: (topic, handler) => {
      const set = handlers.current.get(topic) ?? new Set<Handler>();
      set.add(handler);
      handlers.current.set(topic, set);
      return () => { set.delete(handler); };
    },
  };

  return <LiveContext.Provider value={api}>{children}</LiveContext.Provider>;
}

/**
 * Re-run something when the school reports that kind of change. Typically a
 * query's `reload`, which is stable, so this subscribes once.
 */
export function useLive(topic: Topic, onChange: () => void): void {
  const live = useContext(LiveContext);
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    if (!live) return;
    return live.subscribe(topic, () => latest.current());
  }, [live, topic]);
}
